import { Module } from './../shared/models/module.model';
import { Component, OnInit } from "@angular/core";
import { UIState } from "../shared/state/ui.state";
import { DashboardService } from "../shared/services/dashboard.service";
import { DashboardMessage } from "../shared/models/dashboard.model";
import { switchMap, take, tap } from "rxjs/operators";
import { AppState } from "../shared/state/app.state";
import { AuthService } from "../auth/auth.service";
import { SettingsService } from "../shared/services/settings.service";
import { OperationType } from "../shared/models/user.model";
import { Router } from "@angular/router";
import { DashboardDetail } from "../shared/models/dashboard-detail.model";
import * as L from "leaflet";
import "leaflet.markercluster";
import { InventoryCoordinate } from "../shared/models/inventory-coordinate.model";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { checkedIcon, defaultIcon, dynamicIcon } from "../suspects/suspects-details/constant/defaultIcon";
@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit {
  loading: boolean = false;

  counters: DashboardDetail;
  coordinates: InventoryCoordinate[] = [];

  selectedMessageIndex = 0;
  messages: DashboardMessage[] = [{ Markdown: "Geen boodschappen beschikbaar." }];

  charts: any[] = [];

  moduleUnoccupied: boolean = false;
  moduleNeglected: boolean = false;
  moduleUninhabitable: boolean = false;
  moduleSecondaryResidence: boolean = false;

  labelSecondaryResidence = "";

  modules: Module[] = [];

  get activeModuleCount(): number {
    return this.modules.filter((m) => m.IsActive).length;
  }

  private map: L.Map;
  private markerClusterGroups: { [key: string]: L.MarkerClusterGroup } = {};

  constructor(
    public uiState: UIState,
    private dashboardService: DashboardService,
    public appState: AppState,
    private settingService: SettingsService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.authService
      .hasScreenResource("DASHBOARD", OperationType.READ)
      .pipe(
        take(1),
        tap((hasPermission) => {
          if (!hasPermission || !this.settingService.getSettingValueAsBoolean("module-dashboard")) {
            this.router.navigateByUrl("/dossiers");
            return;
          }

          this.uiState.breadcrumbs$.next([
            {
              name: "Dashboard",
              routerLink: "/dashboard",
            },
          ]);

          this.moduleUnoccupied = Boolean(JSON.parse(this.settingService.getSettingByKey("module-leegstand")?.Value || "0"));
          this.moduleNeglected = Boolean(JSON.parse(this.settingService.getSettingByKey("module-verwaarlozing")?.Value || "0"));
          this.moduleUninhabitable = Boolean(
            JSON.parse(this.settingService.getSettingByKey("module-ongeschikt-onbewoonbaar")?.Value || "0"),
          );
          this.moduleSecondaryResidence = Boolean(
            JSON.parse(this.settingService.getSettingByKey("module-tweede-verblijf")?.Value || "0"),
          );

          this.labelSecondaryResidence = this.settingService.getSettingByKey("label-tweede-verblijven")?.Value || "Tweede Verblijven";

          this.initModules();
          this.fetchData();
        }),
      )
      .subscribe();
  }

  fetchData(): void {
    this.loading = true;

    this.dashboardService
      .getMessages()
      .pipe(
        take(1),
        tap((r) => {
          if (r.length > 0) {
            this.messages = r;
          }
        }),
        switchMap(() => this.dashboardService.getCounters()),
        tap((r) => {
          this.counters = r;
        }),
        switchMap(() => this.dashboardService.getInventoryCoordinates()),
        tap((coordinates) => {
          this.coordinates = coordinates;
        }),
        tap(() => {
          this.loading = false;
          setTimeout(() => {
            this.initMap();
            this.onToggleChange({ value: ["LS"] } as MatButtonToggleChange);
          }, 1000);
        }),
      )
      .subscribe();
  }

  getPercentage(value: number, total: number): number {
    if (total === 0) {
      return 0;
    }
    const percentage = (value / total) * 100;
    return parseFloat(percentage.toFixed(2));
  }

  initMap(): void {
    if (this.map || this.loading) {
      return; // Prevent initialization if map exists or still loading
    }

    this.map = L.map("map").setView([50.8503, 4.3517], 8);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(this.map);
  }

  onToggleChange(event: MatButtonToggleChange) {
    const selectedValues: string[] = event.value;
    const coordinates = this.coordinates.filter(
      (coord) => selectedValues.includes(coord.Type) && coord.Latitude !== 0 && coord.Longitude !== 0,
    );

    this.updateMap(coordinates);
  }

  updateMap(coordinates: InventoryCoordinate[]): void {
    if (!this.map) {
      return;
    }

    coordinates = coordinates.filter(x => !!x.Latitude && x.Latitude !== 0 && !!x.Longitude && x.Longitude !== 0);

    if (this.markerClusterGroups) {
      Object.values(this.markerClusterGroups).forEach(group => {
        group.clearLayers();
        this.map.removeLayer(group);
      });

      this.markerClusterGroups = {};
    }

    const createMarkerClusterGroup = (module: string) => L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        let size = 'small';
        if (count >= 100) {
          size = 'large';
        } else if (count >= 10) {
          size = 'medium';
        }

        return L.divIcon({
          html: `<div class="cluster-icon cluster-${module.toLowerCase()}"><span>${count}</span></div>`,
          className: 'custom-cluster',
          iconSize: L.point(40, 40),
        });
      },
    });

    this.markerClusterGroups['LS'] = createMarkerClusterGroup('LS');
    this.markerClusterGroups['VW'] = createMarkerClusterGroup('VW');
    this.markerClusterGroups['OO'] = createMarkerClusterGroup('OO');
    this.markerClusterGroups['TT'] = createMarkerClusterGroup('TT');

    Object.values(this.markerClusterGroups).forEach(group => {
      this.map.addLayer(group);
    });

    coordinates.forEach((coord) => {
      var icon = dynamicIcon("#1c3243");

      if (coord.Type === "LS") {
        icon = dynamicIcon("#0072B2");
      } else if (coord.Type === "VW") {
        icon = dynamicIcon("#E69F00");
      } else if (coord.Type === "OO") {
        icon = dynamicIcon("#D55E00");
      } else if (coord.Type === "TT") {
        icon = dynamicIcon("#009E73");
      }

      this.markerClusterGroups[coord.Type].addLayer(L.marker([coord.Latitude, coord.Longitude], {icon}));
    });

    if (coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates.map((coord) => [coord.Latitude, coord.Longitude]));
      this.map.fitBounds(bounds);
    }
  }

  initModules(): void {
    this.dashboardService
      .getModules()
      .pipe(
        take(1),
        tap((modules) => {
          this.modules = modules.Data;
        }),
      )
      .subscribe();
  }

  onModuleInfoClick(module: Module): void {
    if (module.InfoUrl) {
      window.open(module.InfoUrl, "_blank");
    }
  }
}
